import React, { useState } from 'react';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';

const ResiForm = () => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formValues, setFormValues] = useState({
        propertyType: '',
        fullAddress: '',
        name: '',
        email: '',
        phone: '',
        additionalNotes: ''
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues(prev => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const validateField = (fieldName, value) => {
        let error = '';
        switch (fieldName) {
            case 'propertyType':
                if (value.length === 0) error = 'Field is required';
                break;
            case 'fullAddress':
                if (value.length === 0) error = 'Address is required';
                break;
            case 'name':
                if (value.length === 0) error = 'Name is required';
                break;
            case 'email':
                if (value.length === 0) error = 'Email is required';
                else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) error = 'Invalid email format';
                break;
            case 'phone':
                if (value.length === 0) error = 'Phone is required';
                else if (!/^\d{3}-\d{3}-\d{4}$/.test(value)) error = 'Invalid phone format (XXX-XXX-XXXX)';
                break;
            default:
                break;
        }
        setErrors(prevErrors => ({ ...prevErrors, [fieldName]: error }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formValues.propertyType || !formValues.fullAddress || !formValues.name || !formValues.email || !formValues.phone) {
            validateField('propertyType', formValues.propertyType);
            validateField('fullAddress', formValues.fullAddress);
            validateField('name', formValues.name);
            validateField('email', formValues.email);
            validateField('phone', formValues.phone);
            return;
        }
        setIsSubmitting(true);
        const inquiryData = {
            serviceType: 'residential',
            dateReceived: new Date().toISOString().split('T')[0],
            propertyType: formValues.propertyType,
            fullAddress: formValues.fullAddress,
            name: formValues.name,
            email: formValues.email,
            phone: formValues.phone,
            additionalNotes: formValues.additionalNotes
        };

        const jsonData = JSON.stringify(inquiryData);
        try {// eslint-disable-next-line
            const response = await axios.post(
                'https://script.google.com/macros/s/AKfycbzPiKp2JO01O-HbgWnJMq6u-SIFs3OT51CrckC4FN2em7azHnawAoEI6rDbss19Nv7o/exec', // Replace with your Google Apps Script URL
                null,
                {
                    params: {
                        jsonData: jsonData
                    }
                }
            );
            toast('Form submitted successfully!');
            setIsSubmitting(false);
            setFormValues({
                propertyType: '',
                fullAddress: '',
                name: '',
                email: '',
                phone: '',
                additionalNotes: ''
            });
            setErrors({});
        } catch (error) {
            toast('Submission failed. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-secondary text-primary p-8 md:p-12 rounded-t-xl shadow-[inset_0px_-8px_25px_0px_rgba(0,0,0,0.25)] flex flex-col gap-6 w-full">
            <Toaster/>
            <h2 className="text-3xl md:text-4xl font-playfair-display text-primary">
                Residential Inquiry
            </h2>
            <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
                {/* Property Type Input */}
                <div className="relative">
                    <motion.input
                        type="text"
                        name="propertyType"
                        value={formValues.propertyType}
                        onChange={handleChange}
                        placeholder="Property Type"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.propertyType
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.propertyType && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.propertyType}
                        </div>
                    )}
                </div>

                {/* Full Address Input */}
                <div className="relative">
                    <motion.input
                        type="text"
                        name="fullAddress"
                        value={formValues.fullAddress}
                        onChange={handleChange}
                        placeholder="Full Address (e.g., 123 Main St, Anytown, CA 12345)"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.fullAddress
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.fullAddress && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.fullAddress}
                        </div>
                    )}
                </div>

                {/* Name Input */}
                <div className="relative">
                    <motion.input
                        type="text"
                        name="name"
                        value={formValues.name}
                        onChange={handleChange}
                        placeholder="Your Name"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.name
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.name && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.name}
                        </div>
                    )}
                </div>

                {/* Email Input */}
                <div className="relative">
                    <motion.input
                        type="email"
                        name="email"
                        value={formValues.email}
                        onChange={handleChange}
                        placeholder="Email"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.email
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.email && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.email}
                        </div>
                    )}
                </div>

                {/* Phone Input */}
                <div className="relative">
                    <motion.input
                        type="text"
                        name="phone"
                        value={formValues.phone}
                        onChange={handleChange}
                        placeholder="Phone Number (XXX-XXX-XXXX)"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.phone
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.phone && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.phone}
                        </div>
                    )}
                </div>

                {/* Additional Notes Input */}
                <div className="relative">
                    <motion.textarea
                        name="additionalNotes"
                        value={formValues.additionalNotes}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Additional Notes"
                        className={`w-full p-2 rounded-lg bg-white border-2 ${errors.additionalNotes
                            ? 'border-red-500'
                            : 'border-primary'}`}
                    />
                    {errors.additionalNotes && (
                        <div className="absolute bottom-[-20px] text-red-500">
                            <i className="fas fa-exclamation-circle mr-1"></i>
                            {errors.additionalNotes}
                        </div>
                    )}
                </div>

                <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className={`px-6 py-3 bg-primary text-accent rounded-md font-nunito-sans text-lg hover:bg-secondary transition ${
                        isSubmitting ? "bg-gray-400 text-gray-200 cursor-not-allowed" : ""
                    }`}
                >
                    {isSubmitting ? "Submitting..." : "Submit"}
                </motion.button>
            </form>
        </div>
    );
};

export default ResiForm;
